import React, { FC, useMemo } from "react"

import { useHistory } from "react-router-dom"

import { APPLICATION, useAuth, useUserProfile } from "@ncs/ncs-api"
import { Box, Button, Icon, Tabs, TabsProps, useUrlState } from "@ncs/web-legos"

import { PageContentWrapper } from "~/shared-components"

import { AccountTab, AlertsTab, BillingTab, InvoicesTab, OrdersTab } from "./components"
import { AccountTabName, type AccountUrlState } from "./types"

export const Account: FC = () => {
	const auth = useAuth()
	const user = auth.user
	const [profile] = useUserProfile(auth.user?.id)

	const isGuest = profile?.isGuest
	const history = useHistory()
	const [{ tab, from }, { updateUrlValue }] = useUrlState<AccountUrlState>({
		tab: AccountTabName.Orders,
		from: null,
	})

	const onLogout = () => {
		history.push("/auth/logout")
	}

	const tabPanels: TabsProps["panels"] = useMemo(() => {
		if (!user) {
			return []
		}

		const panels = [
			{
				navLabel: AccountTabName.Orders,
				component: <OrdersTab />,
				navIcon: <Icon icon="box" />,
			},
		]

		if (!user.apps.includes(APPLICATION.HidePaymentInfo)) {
			panels.push({
				navLabel: AccountTabName.Invoices,
				component: <InvoicesTab />,
				navIcon: <Icon icon="file-invoice-dollar" />,
			})
			if (isGuest) {
				panels.push({
					navLabel: AccountTabName.Billing,
					component: <BillingTab />,
					navIcon: <Icon icon="credit-card" />,
				})
			}
		}

		panels.push(
			{
				navLabel: AccountTabName.Alerts,
				component: <AlertsTab />,
				navIcon: <Icon icon="bell-on" />,
			},
			{
				navLabel: AccountTabName.Account,
				component: <AccountTab />,
				navIcon: <Icon icon="user" />,
			}
		)

		return panels
	}, [user, isGuest])

	// During SSR, don't render the component to avoid hydration issues
	if (typeof window === "undefined") {
		return null
	}

	// Don't render tabs if panels are not ready (e.g., when user is not loaded)
	if (!user || tabPanels.length === 0) {
		return null
	}

	return (
		<PageContentWrapper
			heading="Account"
			breadcrumbs={[{ name: "Account" }]}
			renderRight={() => (
				<Button icon="sign-out" onClick={onLogout}>
					Sign out
				</Button>
			)}
		>
			<Tabs
				currentTab={tab}
				onChange={(newTab) => updateUrlValue("tab", newTab as AccountTabName)}
				panels={tabPanels}
			/>

			{from === "checkout" && (
				<Box mt={2} display="flex" justifyContent="flex-end">
					<Button
						onClick={() => history.push("/shop/checkout")}
						variant="primary-cta"
						icon="long-arrow-right"
					>
						Return to checkout?
					</Button>
				</Box>
			)}
		</PageContentWrapper>
	)
}
